import React, { useEffect, useState } from "react";
import { Box, Typography, Avatar, Tooltip, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { EVENT_FLAG_COLORS } from "../../components/Calendar/types";
import acceptedSvg from "../../assets/meeting/accepted.svg";
import crossSvg from "../../assets/meeting/cross.svg";
import questionSvg from "../../assets/meeting/question.svg";
import {
  extractDomainFromEmail,
  extractNameFromEmail,
} from "../../utils/utils.ts";
import { getUserAttributes } from "../../utils/auth.ts";

interface GuestsProps {
  meetingId?: string;
  attendees: any | null;
  organizer: string;
  attended_participants?: any | null;
  attendedParticipants?: any;
}

const Guests: React.FC<GuestsProps> = ({
  attendees,
  organizer,
  attendedParticipants,
}) => {
  const theme = useTheme();
  const [accountGuests, setAccountGuests] = useState<any[]>([]);
  const [clientGuests, setClientGuests] = useState<any[]>([]);
  const [otherGuests, setOtherGuests] = useState<any[]>([]);
  const loggedInEmail = getUserAttributes()?.email ?? "";
  const loggedInUserDomain = extractDomainFromEmail(loggedInEmail) ?? "";

  useEffect(() => {
    if (attendedParticipants) {
      processAttendees(attendedParticipants);
    }
  }, [attendedParticipants]);

  const processAttendees = (participants: any[] = []) => {
    if (!Array.isArray(participants)) return;

    const allParticipants = Object.values(attendees || {}).flat();

    const filtered = participants.filter(
      (p) => p?.email && (allParticipants.includes(p.email) || p.role === "Organizer")
    );

    const mapToGuest = (p: any) => ({
      email: p?.email ?? "",
      name: p?.name ?? "",
      picture_url: p?.pictureUrl ?? "",
      status: p?.responseStatus ?? "",
      role: p?.role ?? "",
      participantFrom: p?.participantFrom ?? "other",
    });

    setAccountGuests(
      filtered
        .filter((p) => p?.participantFrom === "account")
        .map(mapToGuest)
        .sort((a, b) =>
          a.email === organizer ? -1 : b.email === organizer ? 1 : 0
        )
    );

    setClientGuests(
      filtered
        .filter((p) => p?.participantFrom === "client")
        .map(mapToGuest)
        .sort((a, b) =>
          a.email === organizer ? -1 : b.email === organizer ? 1 : 0
        )
    );

    setOtherGuests(
      filtered
        .filter((p) => p?.participantFrom === "other")
        .map(mapToGuest)
        .sort((a, b) =>
          a.email === organizer ? -1 : b.email === organizer ? 1 : 0
        )
    );
  };

  const renderAttendeeIcon = (status?: string) => {
    if (!status || status === "needsAction") return null;

    const background =
      status?.toLowerCase() === "declined"
        ? "#fa504b"
        : EVENT_FLAG_COLORS[status.toUpperCase() as keyof typeof EVENT_FLAG_COLORS] ||
          "#ccc";

    return (
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          backgroundColor: background,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {status === "accepted" && <img src={acceptedSvg} alt="accepted" />}
        {status === "declined" && <img src={crossSvg} alt="declined" />}
        {status === "tentative" && (
          <img src={questionSvg} alt="tentative" style={{ width: "20px", height: "20px" }} />
        )}
      </Box>
    );
  };

  const renderGuestsSection = (title: string, guests: any[]) => {
    if (!guests || guests.length === 0) return null;

    return (
      <Box sx={{ mb: 3, minWidth: "400px", width: "100%" }}>
        <Box sx={{ mb: 2 }}>
          <Typography className="guest-text">{title}</Typography>
        </Box>

        <Grid container rowSpacing={2} columnSpacing={2}>
          {guests.map((guest, index) => (
            <Grid item xs={6} key={index} className="guest-grid-item">
              {/* Avatar column */}
              <Box className="guest-avatar-wrapper">
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src={guest.picture_url || undefined}
                    alt={guest.name || guest.email || "guest"}
                    className="guest-avatar"
                  />
                  {renderAttendeeIcon(guest.status)}
                </Box>
              </Box>

              {/* Info column */}
              <Box className="guest-info">
                <Tooltip title={guest.email || ""} arrow>
                  <Typography variant="body1" className="guest-name">
                    {extractNameFromEmail(guest?.name ?? "", guest?.email ?? "")}
                  </Typography>
                </Tooltip>
                {organizer === guest.email && (
                  <Typography className="guest-organizer">Organizer</Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <>
      {renderGuestsSection(`${loggedInUserDomain} Invitees :`, accountGuests)}
      {renderGuestsSection("Client Invitees ", clientGuests)}
      {renderGuestsSection("Other Invitees", otherGuests)}

      {!accountGuests.length &&
        !clientGuests.length &&
        !otherGuests.length && (
          <Box className="flex flex-row justify-center items-center w-full">
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: theme.palette.primary.dark,
                fontFamily: 'var(--font-family)',
              }}
            >
              No guests for a meeting
            </Typography>
          </Box>
        )}
    </>
  );
};

export default Guests;
